import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Input,
  Label,
  RadioGroup,
  RadioGroupItem,
} from '@staff-app/ui';
import { useBillingSetDunningExclusion, type DunningExclusionWire } from '../hooks/useBillingConsole';
import { todayCentral } from '@/features/opticlock/lib/month';

/** The Stripe customer-metadata keys this dialog writes. Mirror api `dunning-exclusion-flag.ts`. */
export const DUNNING_EXCLUSION_META_KEY = 'staff-app-dunning-exclusion';
export const DUNNING_EXCLUSION_UNTIL_META_KEY = 'staff-app-dunning-exclusion-until-date';

/** What Stripe holds for each state (strings — Stripe metadata has no booleans). */
export const STOPPED = 'true';
export const PAUSED = 'pause';
export const RESUMED = 'false';

/** Longest pause, matching the server's DUNNING_PAUSE_MAX_YEARS. The server re-checks. */
export const PAUSE_MAX_YEARS = 1;

const NOTE_MAX = 500;

export type DunningDialogMode = 'exclude' | 'resume';
type Choice = 'STOP' | 'PAUSE';

export interface DunningTarget {
  stripeId: string;
  /** What the operator recognises — company, owner email, … — for the title and failures. */
  label: string;
}

/** Stripe's read-back for one customer that was written. */
export interface DunningApplied {
  stripeId: string;
  value: string | null;
  untilDate: string | null;
}

/** `2026-12-31` → `Dec 31, 2026`. A bare calendar day — never shifted through a time zone. */
export function dayLabel(day: string): string {
  const d = dayjs(day, 'YYYY-MM-DD', true);
  return d.isValid() ? d.format('MMM D, YYYY') : day;
}

/**
 * The Stop / Pause-until-a-date / Resume dialog, for ONE customer (Billing Console) or MANY
 * (Dunning Review → Dunning Exclusion List, bulk edit). Requester, 2026-09-23.
 *
 * Writes through the SAME per-customer mutation either way, one call per customer, in order:
 * every customer gets its own Activity Log row and its own read-back, and one refusal does not
 * stop the rest. On a partial failure the dialog stays open listing exactly who failed and why,
 * and the submit button retries only those. `onApplied` reports every customer that WAS
 * written, as soon as the run ends, so the caller can update what it shows.
 *
 * Writes ONLY the two Stripe metadata keys. It does NOT add a Dunning Review (Mongo)
 * exclusion, and staff-app's own dunning ladder does not read these keys; the copy says so.
 */
export function DunningExclusionDialog({
  mode,
  targets,
  onClose,
  onApplied,
  today: todayProp,
  testIdPrefix = 'billing-dunning-exclusion',
}: {
  /** Null = closed. */
  mode: DunningDialogMode | null;
  targets: DunningTarget[];
  onClose: () => void;
  onApplied: (applied: DunningApplied[]) => void;
  /** Today in US Central, `YYYY-MM-DD`. Injectable so specs never depend on the real date. */
  today?: string;
  testIdPrefix?: string;
}) {
  const [setExclusion] = useBillingSetDunningExclusion();
  const [choice, setChoice] = useState<Choice>('STOP');
  const [untilDate, setUntilDate] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  // Who this run still has to write — all of `targets` on open, only the failures after one.
  const [pending, setPending] = useState<DunningTarget[]>(targets);
  const [failures, setFailures] = useState<{ target: DunningTarget; message: string }[]>([]);

  // Reset on every open.
  useEffect(() => {
    if (mode) {
      setChoice('STOP');
      setUntilDate('');
      setNote('');
      setBusy(false);
      setProgress(0);
      setPending(targets);
      setFailures([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const today = todayProp ?? todayCentral();
  const maxDay = dayjs(today, 'YYYY-MM-DD').add(PAUSE_MAX_YEARS, 'year').format('YYYY-MM-DD');

  const trimmed = note.trim();
  const noteValid = trimmed.length > 0 && trimmed.length <= NOTE_MAX;
  const dateValid =
    /^\d{4}-\d{2}-\d{2}$/.test(untilDate) && untilDate >= today && untilDate <= maxDay;
  const canSubmit =
    noteValid && !busy && pending.length > 0 && (mode === 'resume' || choice === 'STOP' || dateValid);

  const id = (s: string) => `${testIdPrefix}-${s}`;
  const many = targets.length > 1;
  const who = many ? `${targets.length} customers` : (targets[0]?.label ?? '');

  const submit = async () => {
    if (!mode || !canSubmit) return;
    const value: DunningExclusionWire = mode === 'resume' ? 'RESUME' : choice;
    setBusy(true);
    setFailures([]);
    setProgress(0);
    const applied: DunningApplied[] = [];
    const failed: { target: DunningTarget; message: string }[] = [];
    for (const t of pending) {
      try {
        const r = (
          await setExclusion({
            variables: {
              stripeId: t.stripeId,
              value,
              comment: trimmed,
              untilDate: value === 'PAUSE' ? untilDate : null,
            },
          })
        ).data?.staffBillingSetDunningExclusion;
        if (!r) throw new Error('No response from staff-api — reload to see what Stripe holds.');
        applied.push({
          stripeId: t.stripeId,
          value: r.dunningExclusion ?? null,
          untilDate: r.dunningExclusionUntil ?? null,
        });
      } catch (e) {
        failed.push({ target: t, message: (e as Error)?.message ?? 'Failed' });
      }
      setProgress((n) => n + 1);
    }
    setBusy(false);
    if (applied.length) onApplied(applied);
    if (failed.length) {
      setFailures(failed);
      setPending(failed.map((f) => f.target));
    } else {
      onClose();
    }
  };

  const writes =
    mode === 'resume'
      ? `${DUNNING_EXCLUSION_META_KEY} = ${RESUMED}, and removes ${DUNNING_EXCLUSION_UNTIL_META_KEY}`
      : choice === 'STOP'
        ? `${DUNNING_EXCLUSION_META_KEY} = ${STOPPED}, and removes ${DUNNING_EXCLUSION_UNTIL_META_KEY}`
        : `${DUNNING_EXCLUSION_META_KEY} = ${PAUSED}, ${DUNNING_EXCLUSION_UNTIL_META_KEY} = ${untilDate || 'YYYY-MM-DD'}`;
  const verb = mode === 'resume' ? 'Resume dunning' : choice === 'STOP' ? 'Stop dunning' : 'Pause dunning';
  const submitLabel = busy
    ? many
      ? `Saving… ${progress}/${pending.length}`
      : 'Saving…'
    : failures.length
      ? `Retry ${pending.length} failed`
      : many
        ? `${verb} for ${targets.length}`
        : verb;

  return (
    <AlertDialog open={mode !== null} onOpenChange={(o) => (o || busy ? null : onClose())}>
      <AlertDialogContent data-testid={id('dialog')}>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {mode === 'resume' ? `Resume dunning for ${who}?` : `Exclude ${who} from dunning`}
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="flex flex-col gap-2 text-xs">
              <span>
                {'Sets Stripe customer metadata '}
                <code className="text-foreground" data-testid={id('writes')}>
                  {writes}
                </code>
                {many
                  ? ` on each of the ${targets.length} customers, one at a time. Nothing else on them changes.`
                  : ` on ${targets[0]?.stripeId ?? ''}. Nothing else on the customer changes.`}
              </span>
              <span>
                {'This flag is read by the dunning tool that looks up Stripe metadata. It does NOT add a Dunning Review exclusion here in staff-app, and staff-app’s own dunning does not read it.'}
              </span>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>

        {mode === 'exclude' ? (
          <RadioGroup value={choice} onValueChange={(v) => setChoice(v as Choice)} className="gap-3">
            <div className="flex items-start gap-2">
              <RadioGroupItem value="STOP" id={id('option-stop')} data-testid={id('option-stop')} />
              <Label htmlFor={id('option-stop')} className="cursor-pointer text-sm font-normal">
                <span className="font-medium">{'Stop'}</span>
                <span className="block text-xs text-muted-foreground">
                  {'Never falls into the dunning process. Stays until someone resumes it.'}
                </span>
              </Label>
            </div>
            <div className="flex items-start gap-2">
              <RadioGroupItem value="PAUSE" id={id('option-pause')} data-testid={id('option-pause')} />
              <Label htmlFor={id('option-pause')} className="cursor-pointer text-sm font-normal">
                <span className="font-medium">{'Pause until a date'}</span>
                <span className="block text-xs text-muted-foreground">
                  {`Excluded through the end of that day (US Central), then dunning resumes automatically. Up to ${PAUSE_MAX_YEARS} year out.`}
                </span>
              </Label>
            </div>
          </RadioGroup>
        ) : null}

        {mode === 'exclude' && choice === 'PAUSE' ? (
          <div className="flex flex-col gap-1">
            <Label htmlFor={id('until')}>{'Paused until'}</Label>
            <Input
              id={id('until')}
              type="date"
              min={today}
              max={maxDay}
              value={untilDate}
              onChange={(e) => setUntilDate(e.target.value)}
              data-testid={id('until')}
            />
            {untilDate && !dateValid ? (
              <p className="text-xs text-destructive" data-testid={id('until-invalid')}>
                {`Pick a day from ${dayLabel(today)} to ${dayLabel(maxDay)}.`}
              </p>
            ) : null}
          </div>
        ) : null}

        <div className="flex flex-col gap-1">
          <Label htmlFor={id('comment')}>{'Note'}</Label>
          <Input
            id={id('comment')}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={mode === 'resume' ? 'e.g. AP paid' : 'e.g. PO in process with AP'}
            data-testid={id('comment')}
          />
          <p className="text-[11px] text-muted-foreground">
            {`Required — the Activity Log is the only record of why; Stripe keeps no history of metadata. ${trimmed.length}/${NOTE_MAX}`}
          </p>
          {failures.length ? (
            <div className="text-xs text-destructive" data-testid={id('error')}>
              {many ? (
                <>
                  <p>{`${targets.length - pending.length} of ${targets.length} updated. These failed:`}</p>
                  <ul className="mt-1 list-disc pl-4" data-testid={id('failures')}>
                    {failures.map((f) => (
                      <li key={f.target.stripeId}>{`${f.target.label} (${f.target.stripeId}): ${f.message}`}</li>
                    ))}
                  </ul>
                </>
              ) : (
                failures[0].message
              )}
            </div>
          ) : null}
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy} data-testid={id('cancel')}>
            {failures.length ? 'Close' : 'Cancel'}
          </AlertDialogCancel>
          <AlertDialogAction
            disabled={!canSubmit}
            onClick={(e) => {
              e.preventDefault();
              void submit();
            }}
            data-testid={id('submit')}
          >
            {submitLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
